// Shared constants for tasks.waddingtonpark.com.au
// Used by both frontend and backend

// API Routes
export const API_ROUTES = {
  TASKS: '/api/tasks',
  TASK_BY_ID: (id: string) => `/api/tasks/${id}`,
  SYNC: '/api/sync',
  AUTH: {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    REFRESH: '/api/auth/refresh',
    PROFILE: '/api/auth/profile',
  },
  MEDIA: {
    UPLOAD: '/api/media/upload',
    PHOTO: (id: string) => `/api/media/photos/${id}`,
    AUDIO: (id: string) => `/api/media/audio/${id}`,
  },
  HEALTH: '/api/health',
} as const;

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

// Error Codes
export const ERROR_CODES = {
  // Authentication
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  UNAUTHORIZED: 'UNAUTHORIZED',
  
  // Validation
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  REQUIRED_FIELD: 'REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',
  VALUE_TOO_LONG: 'VALUE_TOO_LONG',
  VALUE_TOO_SHORT: 'VALUE_TOO_SHORT',
  
  // Tasks
  TASK_NOT_FOUND: 'TASK_NOT_FOUND',
  TASK_ALREADY_EXISTS: 'TASK_ALREADY_EXISTS',
  INVALID_TASK_STATUS: 'INVALID_TASK_STATUS',
  
  // Media
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  MEDIA_NOT_FOUND: 'MEDIA_NOT_FOUND',
  
  // Sync
  SYNC_CONFLICT: 'SYNC_CONFLICT',
  SYNC_FAILED: 'SYNC_FAILED',
  OFFLINE_ERROR: 'OFFLINE_ERROR',
  
  // General
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
} as const;

// File Constraints
export const FILE_CONSTRAINTS = {
  PHOTO: {
    MAX_SIZE: 10 * 1024 * 1024, // 10MB
    ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
    ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp'],
    MAX_WIDTH: 4096,
    MAX_HEIGHT: 4096,
    COMPRESSION_QUALITY: 0.8,
  },
  AUDIO: {
    MAX_SIZE: 50 * 1024 * 1024, // 50MB
    MAX_DURATION: 600, // 10 minutes in seconds
    ALLOWED_TYPES: ['audio/wav', 'audio/mp4', 'audio/mpeg', 'audio/webm'],
    ALLOWED_EXTENSIONS: ['.wav', '.mp4', '.mp3', '.webm'],
    SAMPLE_RATE: 44100,
  },
} as const;

// Sync Configuration
export const SYNC_CONFIG = {
  INTERVAL: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
  BATCH_SIZE: 10,
  TIMEOUT: 30000, // 30 seconds
  OFFLINE_THRESHOLD: 5000, // 5 seconds without response
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_PROFILE: 'user_profile',
  TASKS: 'tasks',
  SYNC_QUEUE: 'sync_queue',
  LAST_SYNC: 'last_sync',
  APP_CONFIG: 'app_config',
  OFFLINE_MODE: 'offline_mode',
  DRAFT_TASK: 'draft_task',
} as const;

// IndexedDB Configuration
export const INDEXEDDB_CONFIG = {
  DATABASE_NAME: 'FarmTasksDB',
  VERSION: 1,
  STORES: {
    TASKS: 'tasks',
    PHOTOS: 'photos',
    AUDIO_RECORDINGS: 'audioRecordings',
    SYNC_QUEUE: 'syncQueue',
    USER_DATA: 'userData',
    SETTINGS: 'settings',
  },
  INDEXES: {
    TASKS: {
      BY_STATUS: 'status',
      BY_CATEGORY: 'category',
      BY_CREATED_AT: 'createdAt',
      BY_SYNC_STATUS: 'syncStatus',
      BY_USER_ID: 'userId',
    },
    PHOTOS: {
      BY_TASK_ID: 'taskId',
      BY_SYNC_STATUS: 'syncStatus',
    },
    AUDIO_RECORDINGS: {
      BY_TASK_ID: 'taskId',
      BY_SYNC_STATUS: 'syncStatus',
    },
    SYNC_QUEUE: {
      BY_TYPE: 'type',
      BY_RESOURCE_TYPE: 'resourceType',
      BY_CREATED_AT: 'createdAt',
    },
  },
} as const;

// PWA Configuration
export const PWA_CONFIG = {
  THEME_COLOR: '#16a34a', // green-600
  BACKGROUND_COLOR: '#ffffff',
  DISPLAY: 'standalone',
  ORIENTATION: 'portrait',
  SCOPE: '/',
  START_URL: '/',
  ICON_SIZES: [72, 96, 128, 144, 152, 192, 384, 512],
  CACHE_STRATEGIES: {
    STATIC: 'CacheFirst',
    API: 'NetworkFirst',
    IMAGES: 'CacheFirst',
    DOCUMENTS: 'StaleWhileRevalidate',
  },
} as const;

// Validation Patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  PHONE: /^\+?[\d\s\-\(\)]{10,}$/,
  UUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  COORDINATES: {
    LATITUDE: /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/,
    LONGITUDE: /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
  },
} as const;

// Default Values
export const DEFAULTS = {
  TASK: {
    PRIORITY: 'medium',
    CATEGORY: 'other',
    ESTIMATED_DURATION: 60, // 1 hour
    STATUS: 'draft',
  },
  PAGINATION: {
    PAGE: 1,
    LIMIT: 20,
    MAX_LIMIT: 100,
  },
  LOCATION: {
    ACCURACY_THRESHOLD: 100, // meters
    TIMEOUT: 10000, // 10 seconds
  },
  MEDIA: {
    PHOTO_QUALITY: 0.8,
    THUMBNAIL_SIZE: 200,
    AUDIO_BITRATE: 128, // kbps
  },
} as const;

// Environment-specific Configuration
export const ENV_CONFIG = {
  DEVELOPMENT: {
    API_BASE_URL: 'http://localhost:3000',
    LOG_LEVEL: 'debug',
    ENABLE_MOCK_DATA: true,
    SYNC_INTERVAL: 10000, // 10 seconds for faster testing
  },
  STAGING: {
    API_BASE_URL: 'https://api-staging.waddingtonpark.com.au',
    LOG_LEVEL: 'info',
    ENABLE_MOCK_DATA: false,
    SYNC_INTERVAL: 30000,
  },
  PRODUCTION: {
    API_BASE_URL: 'https://api.waddingtonpark.com.au',
    LOG_LEVEL: 'error',
    ENABLE_MOCK_DATA: false,
    SYNC_INTERVAL: 60000, // 1 minute
  },
} as const;

// AWS Configuration
export const AWS_CONFIG = {
  REGION: 'ap-southeast-2', // Sydney
  COGNITO: {
    USER_POOL_ID: '', // To be set by runtime config
    CLIENT_ID: '', // To be set by runtime config
  },
  S3: {
    BUCKET_NAME: '', // To be set by runtime config
    REGION: 'ap-southeast-2',
  },
  API_GATEWAY: {
    URL: '', // To be set by runtime config
    STAGE: 'prod',
  },
} as const;

// Feature Flags
export const FEATURE_FLAGS = {
  ENABLE_AUDIO_RECORDING: true,
  ENABLE_LOCATION_TRACKING: true,
  ENABLE_OFFLINE_MODE: true,
  ENABLE_PUSH_NOTIFICATIONS: true,
  ENABLE_BIOMETRIC_AUTH: false, // Future feature
  ENABLE_DARK_MODE: true,
  ENABLE_ANALYTICS: true,
  ENABLE_CRASH_REPORTING: true,
} as const;

// UI Constants
export const UI_CONSTANTS = {
  MOBILE_BREAKPOINT: 768, // px
  TABLET_BREAKPOINT: 1024, // px
  DESKTOP_BREAKPOINT: 1280, // px
  
  ANIMATION_DURATION: {
    FAST: 150, // ms
    NORMAL: 300, // ms
    SLOW: 500, // ms
  },
  
  Z_INDEX: {
    DROPDOWN: 1000,
    MODAL: 1050,
    TOAST: 1100,
    TOOLTIP: 1200,
  },
  
  COLORS: {
    PRIMARY: '#16a34a', // green-600
    SECONDARY: '#64748b', // slate-500
    ACCENT: '#eab308', // yellow-500
    SUCCESS: '#22c55e', // green-500
    WARNING: '#f59e0b', // amber-500
    ERROR: '#ef4444', // red-500
    INFO: '#3b82f6', // blue-500
  },
} as const;

// Performance Thresholds
export const PERFORMANCE_THRESHOLDS = {
  BUNDLE_SIZE_WARNING: 500 * 1024, // 500KB
  BUNDLE_SIZE_ERROR: 1024 * 1024, // 1MB
  API_RESPONSE_WARNING: 2000, // 2 seconds
  API_RESPONSE_ERROR: 5000, // 5 seconds
  MEMORY_USAGE_WARNING: 100 * 1024 * 1024, // 100MB
  MEMORY_USAGE_ERROR: 200 * 1024 * 1024, // 200MB
} as const; 